/*******************************************************************************
  processUpcomingSessionData.js

  Author: Advait;
*******************************************************************************/
import { zonedTimeToUtc, utcToZonedTime, format } from "date-fns-tz";

const MILLISECONDS_IN_24_HOURS = 24 * 60 * 60 * 1000;

// Map facility state types to schedule types
const STATE_TO_TYPE_MAP = {
  INSPECTION: "inspection",
  RECEPTIONIST: "reception",
  SECURITY_PATROL: "security_patrol",
};

const parseDuration = (duration) => {
  if (!duration || duration.includes("undefined") || !duration.toString().trim()) return 30;
  if (duration.includes(":")) {
    const [h, m] = duration.split(":").map(Number);
    return h * 60 + m;
  }
  return parseInt(duration) || 30;
};

/**
 * Match facility state with corresponding scheduled session
 * @param {Object} facilityState - Facility state object {site, tenant, facilitiesState}
 * @param {Array} scheduledRounds - Array of processed scheduled rounds
 * @param {Date} now - Current time
 * @returns {Object} - {matched: boolean, startTime: Date|null, endTime: Date|null, scheduleType: string|null}
 */
const matchFacilityStateWithSchedule = (facilityState, scheduledRounds, now) => {
  const expectedScheduleType = STATE_TO_TYPE_MAP[facilityState.facilitiesState];

  if (!expectedScheduleType) {
    return { matched: false, startTime: null, endTime: null, scheduleType: null };
  }

  // Find scheduled rounds for this site with matching type that overlap with current time
  const matchingRound = scheduledRounds.find((round) => {
    const isSameSite = round.siteName === facilityState.site.siteName;
    const isSameType = round.scheduleType.toLowerCase() === expectedScheduleType.toLowerCase();

    // Check if current time falls within scheduled round
    const endTime = new Date(round.browserTime.getTime() + round.durationMinutes * 60 * 1000);
    const isOngoing = now >= round.browserTime && now <= endTime;
    round.calculatedEndTime = endTime;

    return isSameSite && isSameType && isOngoing;
  });

  if (matchingRound) {
    return {
      matched: true,
      startTime: matchingRound.browserTime,
      endTime: matchingRound.calculatedEndTime,
      scheduleType: matchingRound.scheduleType,
    };
  }

  return { matched: false, startTime: null, endTime: null, scheduleType: null };
};

/**
 * Match facility states with scheduled sessions and add timing information
 * @param {Array} facilityStates - Array of facility state objects
 * @param {Array} scheduledRounds - Array of processed scheduled rounds
 * @param {Date} now - Current time
 * @returns {Array} - Array of facility states with matched schedule timing info
 */
export const matchFacilityStatesWithSchedules = (
  facilityStates,
  scheduledRounds,
  now = new Date()
) => {
  return facilityStates.map((facilityState) => {
    const matchResult = matchFacilityStateWithSchedule(facilityState, scheduledRounds, now);

    return {
      ...facilityState,
      matched: matchResult.matched,
      startTime: matchResult.startTime,
      endTime: matchResult.endTime,
      scheduleType: matchResult.scheduleType,
    };
  });
};

/**
 * Process upcoming session data from schedule data
 * @param {Array} scheduleData - Array of schedule data objects
 * @returns {Array} - Array of scheduled rounds in next 24 hours
 */
export const processUpcomingSessionData = (scheduleData) => {
  if (!scheduleData || !Array.isArray(scheduleData)) return [];

  const fetchTime = new Date(); // Current time in UTC
  const next24Hours = new Date(fetchTime.getTime() + MILLISECONDS_IN_24_HOURS);
  const next24HoursRounds = [];
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  scheduleData.forEach(({ site, tenant, schedules }) => {
    const siteTimezone = site.timezone || "UTC";
    // Get current time in site's timezone to find the right day
    const nowInSiteTime = utcToZonedTime(fetchTime, siteTimezone);

    schedules.forEach((schedule) => {
      const scheduleDay = +schedule.day;
      const duration =
        schedule.duration?.includes("undefined") || !schedule.duration?.trim()
          ? "0:30"
          : schedule.duration;

      // Check yesterday, today, and tomorrow in the site's timezone
      for (let dayOffset = -1; dayOffset <= 1; dayOffset++) {
        const scheduleDate = new Date(nowInSiteTime);
        scheduleDate.setDate(scheduleDate.getDate() + dayOffset);

        // Check if this day matches the schedule's day-of-week
        if (scheduleDate.getDay() !== scheduleDay) continue;

        // Set the schedule time (hour and minute) on this date
        const [startHour, startMinute] = schedule.startTime
          .split(":")
          .map((num) => parseInt(num, 10));
        scheduleDate.setHours(startHour, startMinute, 0, 0);

        // Convert to UTC for all calculations
        const scheduleTimeUTC = zonedTimeToUtc(scheduleDate, siteTimezone);

        // Calculate session end time in UTC
        const sessionEndTimeUTC = new Date(
          scheduleTimeUTC.getTime() + parseDuration(duration) * 60 * 1000
        );

        // include only if within the 24-hour window (ongoing or starting soon)
        if (sessionEndTimeUTC < fetchTime || scheduleTimeUTC > next24Hours) continue;

        next24HoursRounds.push({
          tenantName: tenant.company,
          siteName: site.siteName,
          scheduleType: schedule.type,
          scheduledTime: scheduleDate, // in site timezone
          siteTimezone,
          browserTimezone,
          browserTime: scheduleTimeUTC, // browser time
          duration,
          durationMinutes: parseDuration(duration),
          sessionEndTimeUTC, // End time in UTC
        });
      }
    });
  });

  // Sort by browser time
  next24HoursRounds.sort((a, b) => a.browserTime - b.browserTime);

  return next24HoursRounds;
};

/**
 * Generate 24 hour time slots starting from current hour
 * @param {number} currentHour - Current hour (0-23)
 * @returns {Array} - Array of time slot objects
 */
export const generateTimeSlots = (currentHour) => {
  return Array.from({ length: 24 }, (_, i) => {
    const hour = (currentHour + i) % 24;
    const isPM = hour >= 12;
    const displayHour = hour % 12 || 12; // Convert 0-23 to 1-12 (0 -> 12, 13 -> 1, etc.)
    return {
      hour,
      timeString: `${displayHour} ${isPM ? "PM" : "AM"}`,
      isCurrentHour: i === 0,
    };
  });
};

/**
 * Get status for a specific time slot
 * @param {number} hour - Hour of the slot
 * @param {number} slotIndex - Index of the slot
 * @param {number} currentHour - Current hour
 * @param {Array} scheduledRounds - Array of scheduled rounds
 * @param {Array} ongoingSessions - Array of current ongoing robot sessions
 * @returns {Object|null} - Status object or null if no status
 */
export const getSlotStatus = (
  hour,
  slotIndex,
  currentHour,
  scheduledRounds,
  ongoingSessions = []
) => {
  const now = new Date();

  // For each round, calculate slot start/end in browser time
  let hourRounds = scheduledRounds
    .map((round) => {
      const slotStartBrowser = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hour,
        0,
        0,
        0
      );
      if (currentHour + slotIndex >= 24) {
        slotStartBrowser.setDate(slotStartBrowser.getDate() + 1);
      }
      const slotEndBrowser = new Date(slotStartBrowser.getTime() + 3600000);

      const roundEnd = new Date(round.browserTime.getTime() + round.durationMinutes * 60 * 1000);

      return {
        ...round,
        roundStart: round.browserTime,
        roundEnd,
        slotStartBrowser,
        slotEndBrowser,
      };
    })
    .filter((round) => {
      return (
        round.roundStart < round.slotEndBrowser && round.roundEnd > round.slotStartBrowser
      );
    });

  // Sort hourRounds by browser time
  hourRounds.sort((a, b) => a.roundStart - b.roundStart);

  // Calculate slot start/end time for this specific slot
  const slotStartBrowser = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hour,
    0,
    0,
    0
  );
  if (currentHour + slotIndex >= 24) {
    slotStartBrowser.setDate(slotStartBrowser.getDate() + 1);
  }
  const slotEndBrowser = new Date(slotStartBrowser.getTime() + 3600000);

  const ongoingSessionsInSlot = ongoingSessions.filter((session) => {
    if (session.matched && session.startTime && session.endTime) {
      return session.startTime < slotEndBrowser && session.endTime > slotStartBrowser;
    }
    return slotIndex === 0;
  });

  const hasOngoingSessions = ongoingSessionsInSlot.length > 0;

  if (!hourRounds.length && !hasOngoingSessions) {
    return { text: "", tooltip: "" };
  }

  // Filter scheduled rounds that are already ongoing
  const scheduledOnly = hourRounds.filter((round) => {
    // Check if this round is already running (has an ongoing session)
    const alreadyOngoing = ongoingSessionsInSlot.some((session) => {
      if (!session.matched) return false;
      const expectedType = STATE_TO_TYPE_MAP[session.facilitiesState];
      return (
        session.site.siteName === round.siteName &&
        expectedType?.toLowerCase() === round.scheduleType.toLowerCase()
      );
    });
    // Only include rounds that are NOT ongoing
    return !alreadyOngoing;
  });

  // Build tooltip
  let tooltip = "";

  // Show ongoing sessions that are active in this slot
  if (hasOngoingSessions) {
    tooltip += ongoingSessionsInSlot
      .map((session) => {
        const displayType =
          STATE_TO_TYPE_MAP[session.facilitiesState] || session.facilitiesState;
        let sessionText = `${session.tenant.company} - ${session.site.siteName}\n${displayType}`;

        // Show end time only if there is an end time
        if (session.matched && session.endTime) {
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const endTimeFormatted = format(session.endTime, "hh:mm a", {
            timeZone: browserTimezone,
          });
          sessionText += `\nestimated end time: ${endTimeFormatted}`;
        }

        return sessionText;
      })
      .join("\n\n");
  }

  // Always show scheduled rounds if present
  if (scheduledOnly.length > 0) {
    if (tooltip) tooltip += "\n-------------------------\n\nScheduled Sessions:\n";
    tooltip += scheduledOnly
      .map((round) => {
        // Browser time - roundStart/roundEnd are already in browser timezone, just format them
        const startTimeBrowser = format(round.roundStart, "hh:mm a zzz", {
          timeZone: round.browserTimezone,
        });
        const endTimeBrowser = format(round.roundEnd, "hh:mm a zzz", {
          timeZone: round.browserTimezone,
        });

        // Only show site time if it's different from browser timezone
        let siteTimeStr = "";
        if (round.siteTimezone !== round.browserTimezone) {
          const startTimeSite = format(round.scheduledTime, "hh:mm a zzz", {
            timeZone: round.siteTimezone,
          });
          const roundEndSite = new Date(
            round.scheduledTime.getTime() + round.durationMinutes * 60 * 1000
          );
          const endTimeSite = format(roundEndSite, "hh:mm a zzz", {
            timeZone: round.siteTimezone,
          });
          siteTimeStr = `\nSite Time: ${startTimeSite} - ${endTimeSite} (${round.siteTimezone})`;
        }

        return `${round.tenantName} - ${round.siteName}\n${round.scheduleType}\n${startTimeBrowser} - ${endTimeBrowser} (estimated duration: ${round.duration})${siteTimeStr}`;
      })
      .join("\n\n");
  }

  // determine status text
  let text = "";
  if (hasOngoingSessions) {
    text = "Ongoing session(s)";
  } else if (scheduledOnly.length > 0) {
    text = "Scheduled session(s)";
  }

  return {
    text,
    tooltip,
  };
};
